import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createSetting,
  deleteSetting,
  getSettings,
  getSidemenu,
  getTargetGroupsSetting,
  getVisibleSettings,
  updateSetting,
  updateSettingJson,
} from "./settings";
import { settingsQueryKeys } from "./query-keys";
import { extractTargetGroups } from "./transformers";

function invalidateSettingsLists(queryClient) {
  queryClient.invalidateQueries({ queryKey: settingsQueryKeys.lists() });
  queryClient.invalidateQueries({ queryKey: settingsQueryKeys.sidemenu() });
  queryClient.invalidateQueries({ queryKey: settingsQueryKeys.targetGroups() });
}

export function useGetSettings() {
  return useQuery({
    queryKey: settingsQueryKeys.list(),
    queryFn: getSettings,
  });
}

export function useGetVisibleSettings() {
  return useQuery({
    queryKey: settingsQueryKeys.visible(),
    queryFn: getVisibleSettings,
  });
}

export function useGetSidemenu(enabled = true) {
  return useQuery({
    queryKey: settingsQueryKeys.sidemenu(),
    queryFn: getSidemenu,
    enabled,
    select: (data) =>
      (data?.settings_json || []).filter((item) => item.active),
  });
}

export function useGetTargetGroupsSetting() {
  return useQuery({
    queryKey: settingsQueryKeys.targetGroups(),
    queryFn: getTargetGroupsSetting,
    select: extractTargetGroups,
  });
}

export function useCreateSettingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createSetting,
    onSuccess: () => invalidateSettingsLists(queryClient),
  });
}

export function useUpdateSettingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateSetting,
    onSuccess: () => invalidateSettingsLists(queryClient),
  });
}

export function useUpdateSettingJsonMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ settingsKey, settings_json }) =>
      updateSettingJson(settingsKey, settings_json),
    onSuccess: () => invalidateSettingsLists(queryClient),
  });
}

export function useDeleteSettingMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteSetting,
    onSuccess: () => invalidateSettingsLists(queryClient),
  });
}
